import React, { useEffect, useState } from "react";
import { MapPinned, Loader2, Trash2, Navigation, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { createLocationShare, fetchMyLocationShares, revokeLocationShare, fetchReceivedLocationShares, fetchProviderLocationShares, replyLocationShare, fetchLocationShareDirections, reverseGeocode, formatApiError } from "@/lib/api";
import { getCurrentPosition, openDirections } from "@/components/maps/geo";

export const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
export const live = (l) => (l || []).filter((s) => !s.expires_at || Date.parse(s.expires_at) > Date.now());
export const GONE = "Location sharing band ho chuki hai ya 24 ghante ki validity khatam ho gayi.";
export const btn = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold min-h-[36px] disabled:opacity-60";
export const primary = `${btn} bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-900 text-white`;
export const danger = `${btn} border border-rose-200 text-rose-700 hover:bg-rose-50`;

// Each failure stage gets its own message: permission/GPS (geo.js text), save failure, expired (404), network.
export const apiFail = (e, action) => {
  if (!e?.response) return `Network issue — ${action} nahi ho payi. Internet check karke dobara try karein.`;
  if (e.response.status === 404) return formatApiError(e) || GONE;
  return `${action} nahi ho payi: ${formatApiError(e)}`;
};

// GPS -> optional locality label. Geo errors are already user-facing (permission denied / timeout / unavailable).
export async function captureLocation() {
  let p;
  try { p = await getCurrentPosition(); } catch (e) { throw new Error(e.message || "Location capture nahi ho payi."); }
  if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lng)) throw new Error("Location capture nahi ho payi. Dobara try karein.");
  const geo = await reverseGeocode(p.lat, p.lng).catch(() => null);
  return { lat: p.lat, lng: p.lng, label: geo?.locality || "" };
}

// Re-validated on every tap; the tab is opened inside the tap so it isn't popup-blocked.
export async function goTo(shareId, onGone) {
  const w = window.open("", "_blank");
  if (w) w.opener = null;
  try {
    const { directions_url } = await fetchLocationShareDirections(shareId);
    if (w) w.location.href = directions_url; else openDirections(directions_url);
  } catch (e) {
    if (w) w.close();
    if (e?.response?.status === 404) { onGone?.(); toast.info(GONE); } else toast.error(apiFail(e, "Location verify"));
  }
}

function ProviderSharedLocation({ serviceId, userId }) {
  const [items, setItems] = useState([]);
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    const load = () => fetchProviderLocationShares(serviceId).then((l) => alive && setItems(live(l))).catch(() => {});
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 30000);
    window.addEventListener("vb:location-pulse", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("vb:location-pulse", load); };
  }, [userId, serviceId]);
  const s = items[0];
  if (!s) return null;
  const go = async () => { setOpening(true); await goTo(s.id, () => setItems([])); setOpening(false); };
  return (
    <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid="provider-location-received">
      <div className="text-sm font-semibold text-slate-900">{s.provider_name || "Provider"} ne aapke saath location share ki hai</div>
      <div className="text-xs text-slate-500">{s.label ? `${s.label} · ` : ""}{fmt(s.expires_at)} tak (IST)</div>
      <button type="button" onClick={go} disabled={opening} className={`${primary} mt-2`} data-testid="provider-location-directions">{opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />} Get Directions to Provider</button>
    </div>
  );
}

// Customer side: explicit, revocable 24h share with this listing's provider; also shows the provider's reply.
export function ShareLocationCard({ serviceId, isOwner }) {
  const { user } = useApp();
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    if (!user || isOwner) return;
    fetchMyLocationShares(serviceId).then((l) => { setShare(live(l)[0] || null); setLoadError(""); })
      .catch((e) => setLoadError(apiFail(e, "Aapki share ki jaankari load")));
  }, [user?.id, serviceId, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps
  if (isOwner) return null;

  const doShare = async () => {
    if (!user) { window.dispatchEvent(new Event("vb:open-auth")); return; }
    if (!window.confirm("Aapki current location sirf is provider ko 24 ghante ke liye dikhegi, taaki woh aap tak pahunch sake. Share karein?")) return;
    setBusy(true);
    try {
      const loc = await captureLocation();
      try { setShare(await createLocationShare({ service_id: serviceId, ...loc })); }
      catch (e) { throw new Error(apiFail(e, "Location save")); }
      toast.success("Location share ho gayi (24 ghante ke liye)");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await revokeLocationShare(share.id); setShare(null); toast.success("Location sharing band kar di"); }
    catch (e) { if (e?.response?.status === 404) { setShare(null); toast.info(GONE); } else toast.error(apiFail(e, "Sharing band")); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 min-w-0" data-testid="share-location-card">
      <div className="flex items-start gap-2">
        <MapPinned className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-slate-700 flex-1 min-w-0 break-words">
          {share ? (
            <>
              <div className="font-semibold" data-testid="share-location-active">Aapki location is provider ke saath share hai</div>
              <div className="text-xs text-slate-500">{share.label ? `${share.label} · ` : ""}{fmt(share.expires_at)} tak (IST)</div>
            </>
          ) : (
            <>
              <div className="font-semibold">Provider ko ghar/kaam ki jagah bulana hai?</div>
              <div className="text-xs text-slate-500">Apni current location sirf is provider ke saath 24 ghante ke liye share karein. Kabhi bhi hata sakte hain.</div>
            </>
          )}
          {loadError && <div className="text-xs text-rose-700 mt-1" data-testid="share-location-load-error">{loadError}</div>}
        </div>
      </div>
      <div className="mt-3">
        {share
          ? <button type="button" onClick={revoke} disabled={busy} className={danger} data-testid="share-location-revoke">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Sharing band karein</button>
          : <button type="button" onClick={doShare} disabled={busy} className={primary} data-testid="share-location-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPinned className="w-3.5 h-3.5" />} Apni location share karein</button>}
      </div>
      {user && <ProviderSharedLocation serviceId={serviceId} userId={user.id} />}
    </div>
  );
}

export function ReceivedRow({ s, onGone, onReply }) {
  const [busy, setBusy] = useState("");
  const reply = s.my_reply && Date.parse(s.my_reply.expires_at) > Date.now() ? s.my_reply : null;
  const go = async () => { setBusy("go"); await goTo(s.id, onGone); setBusy(""); };
  const send = async () => {
    if (!window.confirm(`Aapki current location sirf ${s.customer_name || "is customer"} ko 24 ghante ke liye dikhegi. Bhejein?`)) return;
    setBusy("send");
    try {
      const loc = await captureLocation();
      try { onReply(await replyLocationShare(s.id, loc)); }
      catch (e) { if (e?.response?.status === 404) { onGone(); } throw new Error(apiFail(e, "Location save")); }
      toast.success("Aapki location customer ko bhej di gayi (24 ghante)");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  };
  const stop = async () => {
    setBusy("stop");
    try { await revokeLocationShare(reply.id); onReply(null); toast.success("Aapki location sharing band kar di"); }
    catch (e) { if (e?.response?.status === 404) { onReply(null); toast.info(GONE); } else toast.error(apiFail(e, "Sharing band")); }
    finally { setBusy(""); }
  };
  const spin = (k, Icon) => (busy === k ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />);
  return (
    <div className="py-2 flex flex-wrap items-center gap-2" data-testid={`received-location-${s.id}`}>
      <div className="flex-1 min-w-0 basis-full sm:basis-auto text-sm">
        <div className="font-semibold text-slate-900 truncate">{s.customer_name} · {s.service_name}</div>
        <div className="text-xs text-slate-500 truncate">{s.label ? `${s.label} · ` : ""}{fmt(s.expires_at)} tak</div>
        {reply && <div className="text-xs text-emerald-700" data-testid={`received-location-reply-active-${s.id}`}>Aapki location bheji gayi · {fmt(reply.expires_at)} tak</div>}
      </div>
      <button type="button" onClick={go} disabled={!!busy} className={primary} data-testid={`received-location-directions-${s.id}`}>{spin("go", Navigation)} Get Directions to Customer</button>
      {reply
        ? <button type="button" onClick={stop} disabled={!!busy} className={danger} data-testid={`received-location-reply-stop-${s.id}`}>{spin("stop", Trash2)} Meri location band karein</button>
        : <button type="button" onClick={send} disabled={!!busy} className={`${btn} border border-emerald-200 text-emerald-800 hover:bg-emerald-50`} data-testid={`received-location-reply-${s.id}`}>{spin("send", Send)} Apni location bhejein</button>}
    </div>
  );
}

// Provider side: list re-validated with the backend (poll + focus + reconnect); every directions use is re-checked server-side.
const POLL_MS = 15000;
export function ReceivedLocations({ children }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    const load = () => fetchReceivedLocationShares().then((l) => { if (alive) { setItems(live(l)); setError(""); } })
      .catch((e) => { if (alive) { setItems((p) => p || []); setError(apiFail(e, "Shared locations load")); } });
    load();
    const t = setInterval(() => { if (document.visibilityState === "visible" && navigator.onLine !== false) load(); }, POLL_MS);
    const onVis = () => document.visibilityState === "visible" && load();
    window.addEventListener("online", load); window.addEventListener("focus", load); window.addEventListener("vb:location-pulse", load); document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(t); window.removeEventListener("online", load); window.removeEventListener("focus", load); window.removeEventListener("vb:location-pulse", load); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  useEffect(() => {
    const t = setInterval(() => setItems((l) => (l ? live(l) : l)), 30000);
    return () => clearInterval(t);
  }, []);
  if (items === null) return null;
  const drop = (id) => setItems((l) => (l || []).filter((x) => x.id !== id));
  const setReply = (id, r) => setItems((l) => (l || []).map((x) => (x.id === id ? { ...x, my_reply: r } : x)));
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 min-w-0" data-testid="received-locations">
      <div className="font-display font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Customers ki shared location</div>
      {error && <p className="text-xs text-rose-700 mt-1" data-testid="received-locations-error">{error}</p>}
      {!items.length
        ? <p className="text-xs text-slate-500 mt-1" data-testid="received-locations-empty">Abhi kisi customer ne location share nahi ki. Customer aapki service page se "Apni location share karein" daba kar share kar sakta hai.</p>
        : <div className="mt-2 divide-y divide-slate-100">{items.map((s) => <ReceivedRow key={s.id} s={s} onGone={() => drop(s.id)} onReply={(r) => setReply(s.id, r)} />)}</div>}
      {children}
    </div>
  );
}
